import { Injectable } from '@angular/core';
import { isAdmin } from '@app/core/misc/isAdmin';
import { logToConsole } from '@app/core/misc/rxjs-operators';
import { MessagingService } from '@app/core/services/messaging.service';
import { NotesService } from '@app/core/services/notes.service';
import { UserService } from '@app/core/services/user.service';
import { showTodoDialog } from '@app/shared/misc/showTodoDialog';
import { createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store/src/models';
import { DialogService } from 'primeng/dynamicdialog';
import { EMPTY, Observable, audit, filter, first, interval, map, mergeMap, scan, switchMap } from 'rxjs';
import { TranslatableEffect } from '../../i18n/TranslatableEffects';
import { ClientActions } from '../clients/clients.actions';
import { ClientSelectors } from '../clients/clients.selectors';
import { ListActions } from '../list/list.actions';
import { ToastActions } from '../toast/toast.actions';
import { TodoActions } from '../todo/todo.actions';
import { MessagingMessage } from '../types';
import { MessagingActions } from './messaging.actions';
import { BLAPP_PROVIDER_KEY } from './messaging.constants';
import { MessageIds, NewClientOnboardedMessage } from './messaging.ids';
import { MessagingSelectors } from './messaging.selectors';

const sampleInterval = 1 * 60 * 1000;

@Injectable()
export class MessagingEffects extends TranslatableEffect {
  processMessages$ = createEffect(() => this.messagingService.messages$.pipe(mergeMap((m) => this.handleMessage(m))));

  connecting$ = createEffect(
    () => this.actions$.pipe(ofType(MessagingActions.connecting), logToConsole('Messaging: Connecting...')),
    { dispatch: false },
  );

  connected$ = createEffect(
    () => this.actions$.pipe(ofType(MessagingActions.connected), logToConsole('Messaging: Connected!')),
    { dispatch: false },
  );

  disconnected$ = createEffect(
    () => this.actions$.pipe(ofType(MessagingActions.disconnected), logToConsole('Messaging: Disconnected!')),
    { dispatch: false },
  );

  subscribing$ = createEffect(
    () => this.actions$.pipe(ofType(MessagingActions.subscribing), logToConsole('Messaging: Subscribing...')),
    { dispatch: false },
  );

  subscribed$ = createEffect(
    () => this.actions$.pipe(ofType(MessagingActions.subscribed), logToConsole('Messaging: Subscribed!')),
    { dispatch: false },
  );

  unsubscribed$ = createEffect(
    () => this.actions$.pipe(ofType(MessagingActions.unsubscribed), logToConsole('Messaging: Unsubscribed!')),
    { dispatch: false },
  );

  errors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagingActions.error),
      scan((acc, ctx) => ({ count: acc.count + 1, ctx }), { count: 0, ctx: {} }),
      audit((data) => (data.count === 1 ? interval(0) : interval(sampleInterval))),
      map((data) => data.ctx),
      logToConsole('Messaging: Error', true),
      map(() =>
        ToastActions.showWarnMessage({
          summary: this.translate('cantConnectToMessageServiceHead'),
          detail: this.translate('cantConnectToMessageServiceText'),
        }),
      ),
    ),
  );

  private handleMessage = (message: MessagingMessage) => {
    if (!this.handlers[message.data.messageId]) {
      return EMPTY;
    }

    return this.handlers[message.data.messageId](message);
  };

  private handleUserWithoutAssignedTeamMessage = (message: MessagingMessage) =>
    this.store.select(MessagingSelectors.firmAndUserSelector).pipe(
      first(),
      filter((props) => Boolean(props)),
      filter(({ user }) => user.id !== message.data.userId && isAdmin(user)),
      switchMap(() => this.userService.getUser(message.data.userId)),
      map((user) =>
        ToastActions.showWarnMessage({
          summary: this.translate('userWithNoTeamLoggedIn'),
          detail: `${user.initials} - ${user.name}`,
          sticky: true,
          life: 60 * 60 * 24 * 1000,
        }),
      ),
    );

  private handleUserReceivedTodoMessage = (message: MessagingMessage<{ authorId: number; noteId: number }>) =>
    this.store.select(MessagingSelectors.firmAndUserSelector).pipe(
      first(),
      filter((props) => Boolean(props)),
      filter(({ user }) => user.id === message.data.userId),
      switchMap(() => this.userService.getUser(message.data.props.authorId)),
      mergeMap((user) => [
        TodoActions.increaseTodoCount(),
        ToastActions.showInfoMessage({
          summary: this.translate('youHaveNewTodoItem'),
          detail: this.translate('youHaveNewTodoItemFrom', { params: { name: user.name } }),
          sticky: true,
          life: 60 * 60 * 24 * 1000,
          button: {
            text: this.translate('openTodoItem'),
            click: () =>
              showTodoDialog({
                deps: {
                  dialogService: this.dialogService,
                  notesService: this.notesService,
                  translationService: this.translationService,
                  store: this.store,
                },
                id: message.data.props.noteId,
              }).subscribe(),
          },
        }),
      ]),
    );

  private handleUserReceivedUpdatedTodoMessage = (message: MessagingMessage<{ authorId: number; noteId: number }>) =>
    this.store.select(MessagingSelectors.firmAndUserSelector).pipe(
      first(),
      filter((props) => Boolean(props)),
      filter(({ user }) => user.id === message.data.userId),
      switchMap(() => this.userService.getUser(message.data.props.authorId)),
      map((user) =>
        ToastActions.showInfoMessage({
          summary: this.translate('todoItemHasChangedHead'),
          detail: this.translate('todoItemHasChangedText', { params: { name: user.name } }),
          sticky: true,
          life: 60 * 60 * 24 * 1000,
          button: {
            text: this.translate('openTodoItem'),
            click: () =>
              showTodoDialog({
                deps: {
                  dialogService: this.dialogService,
                  notesService: this.notesService,
                  translationService: this.translationService,
                  store: this.store,
                },
                id: message.data.props.noteId,
              }).subscribe(),
          },
        }),
      ),
    );

  private handleClientOnboardedMessage = (message: MessagingMessage<NewClientOnboardedMessage>) => {
    if (message.data.props.providerKey !== BLAPP_PROVIDER_KEY) {
      return EMPTY;
    }

    return this.store.select(ClientSelectors.allClients).pipe(
      first(),
      mergeMap((all) => {
        const client = all.find((c) => c.id === message.data.props.clientId);
        if (!client || client.archived) {
          return EMPTY;
        }

        const cloudApiKey = message.data.props.publicKey;
        const item = { ...client, cloudApiKey };
        const actions = [
          ToastActions.showInfoMessage({
            summary: this.translate('companyHasConnectedWithPackage'),
            detail: this.translate('companyHasConnectedWithPackageText', { params: { clientName: client.name } }),
          }),
          ClientActions.updateCludApiKey({ id: client.id, cloudApiKey }),
        ];
        const allActions = window.location.pathname.includes('clients')
          ? [
              ...actions,
              ListActions.updateItemInList({ item }),
              ListActions.updateItemInListSucceeded({ item, predicate: (i) => i.id === item.id }),
            ]
          : actions;
        return allActions;
      }),
    );
  };

  private handlers: Record<string, (message: MessagingMessage) => Observable<Action>> = {
    [MessageIds.USER_WITHOUT_TEAM_LOGGED_IN]: this.handleUserWithoutAssignedTeamMessage,
    [MessageIds.USER_RECEIVED_TODO]: this.handleUserReceivedTodoMessage,
    [MessageIds.USER_TODO_UPDATED_BY_OTHER_USER]: this.handleUserReceivedUpdatedTodoMessage,
    [MessageIds.NEW_CLIENT_ONBOARDED]: this.handleClientOnboardedMessage,
  };

  // eslint-disable-next-line max-params
  constructor(
    private messagingService: MessagingService,
    private userService: UserService,
    private dialogService: DialogService,
    private notesService: NotesService,
  ) {
    super('MessagingEffects');
  }
}
